import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import type { PaginationState } from '@tanstack/react-table'
import { getCoreRowModel, getPaginationRowModel } from '@tanstack/react-table'
import { SlidersHorizontal } from 'lucide-react'
import { DataTable } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination'
import { Button } from '@/components/ui/button'

import { buildColumns } from './columns'
import { ActivityFilterPanel } from './ActivityFilterPanel'
import type { ActivityFilterValues } from './ActivityFilterPanel'
import { fetchActivityLogs } from '@/api/activity.api'
import { formatEndDate, formatStartDate } from '@/utils/format'

const DEFAULT_FILTER: ActivityFilterValues = {
  unitId: undefined,
  unitName: undefined,
  robotId: undefined,
  robotName: undefined,
  time: undefined,
}

export function ActivityPage() {
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const [showFilter, setShowFilter] = useState(false)
  const [filter, setFilter] = useState<ActivityFilterValues>(DEFAULT_FILTER)
  const [activeFilters, setActiveFilters] =
    useState<ActivityFilterValues>(DEFAULT_FILTER)

  const { data, isLoading } = useQuery({
    queryKey: ['activity-logs', pageIndex, pageSize, activeFilters],
    queryFn: ({ signal }) => {
      return fetchActivityLogs(
        pageIndex,
        pageSize,
        {
          unitId: activeFilters.unitId,
          robotId: activeFilters.robotId,
          fromDate: formatStartDate(activeFilters.time?.from) || undefined,
          toDate: formatEndDate(activeFilters.time?.to) || undefined,
        },
        signal,
      )
    },
    refetchOnMount: 'always',
  })

  const columns = buildColumns({ pageIndex, pageSize })

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between">
        <h1 className="text-2xl font-bold text-gray-900">LỊCH SỬ HOẠT ĐỘNG</h1>
        <div className="flex items-center gap-2 py-4">
          <Button
            variant="outline"
            size="md"
            className="gap-2"
            onClick={() => setShowFilter((v) => !v)}
          >
            <SlidersHorizontal size={16} />
            Bộ lọc
          </Button>
        </div>
      </div>

      {showFilter && (
        <ActivityFilterPanel
          values={filter}
          onChange={setFilter}
          onSearch={() => {
            setActiveFilters(filter)
            setPagination((s) => ({ ...s, pageIndex: 0 }))
          }}
        />
      )}

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        isLoading={isLoading}
        tableOptions={{
          state: { pagination: { pageIndex, pageSize } },
          onPaginationChange: setPagination,
          getPaginationRowModel: getPaginationRowModel(),
          getCoreRowModel: getCoreRowModel(),
          manualPagination: true,
          pageCount: data?.totalPages,
        }}
        footer={
          <Pagination
            page={pageIndex + 1}
            pageCount={data?.totalPages ?? 1}
            pageSize={pageSize}
            totalCount={data?.total ?? 0}
            onPageChange={(p) =>
              setPagination((s) => ({ ...s, pageIndex: p - 1 }))
            }
            onPageSizeChange={(size) =>
              setPagination({ pageIndex: 0, pageSize: size })
            }
          />
        }
      />
    </section>
  )
}
